import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { useState, useEffect } from 'react';
import { pluralize } from '@/utils/format';
import { AssetThumbnailGrid } from '@/components/AssetThumbnailGrid/AssetThumbnailGrid';
import { useMoveAssetsToGroup } from '@/hooks/api/reports/useMoveAssetsToGroup';
import { useToast } from '@/hooks/useToast';
import { DestinationGroupAutocomplete } from './MergeGroupModal/DestinationGroupAutocomplete';
import { useInfiniteReportGroups } from '@/hooks/api/reports/useInfiniteReportGroups';

export const MoveToGroupModal = ({
  open,
  onClose,
  onSuccess,
  currentGroupId,
  assetIds,
  reportId,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  currentGroupId: string;
  assetIds: string[];
  reportId: string;
}) => {
  const [selectedGroupId, setSelectedGroupId] = useState<string | undefined>();
  const showToast = useToast(state => state.showToast);

  const { groups, loadAll, isFetchingNextPage } = useInfiniteReportGroups({ id: reportId });

  useEffect(() => {
    if (open) {
      loadAll();
    }
  }, [open, loadAll]);

  const { mutate: moveAssets, isPending: isLoading } = useMoveAssetsToGroup({
    reportId,
    onSuccess: () => {
      showToast({ message: 'Assets moved successfully', type: 'success' });
      onSuccess?.();
      handleModalClose();
    },
    onError: (error: string) => {
      showToast({ message: error, type: 'error' });
    },
  });

  const availableGroups = groups.filter(group => group.id !== currentGroupId);
  const currentGroup = groups.find(group => group.id === currentGroupId);
  const assetCount = assetIds.length;

  const handleMove = () => {
    if (selectedGroupId) {
      moveAssets({
        assetIds,
        sourceGroupId: currentGroupId,
        targetGroupId: selectedGroupId,
      });
    }
  };

  const handleModalClose = () => {
    setSelectedGroupId(undefined);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleModalClose}
      maxWidth='sm'
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: '12px',
          },
        },
      }}
    >
      <DialogTitle>Move {pluralize(assetCount, 'Asset', 'Assets')} to Group</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            mb: 2,
            p: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '6px',
            backgroundColor: 'action.hover',
          }}
        >
          <Typography variant='subtitle2' sx={{ mb: 1, fontWeight: 600 }}>
            Moving {pluralize(assetCount, 'asset', 'assets')}
          </Typography>
          <AssetThumbnailGrid assetIds={assetIds} tenantId={currentGroup?.tenantId || ''} thumbnailSize={48} />
        </Box>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          Select destination group:
        </Typography>
        <Box>
          <DestinationGroupAutocomplete
            availableGroups={availableGroups}
            allGroups={groups}
            selectedGroupId={selectedGroupId}
            onGroupSelect={setSelectedGroupId}
            isLoadingGroups={isFetchingNextPage}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleModalClose} color='primary' disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleMove}
          variant='contained'
          color='primary'
          disabled={!selectedGroupId || isLoading}
          sx={{
            backgroundColor: 'secondary.main',
            '&:hover': {
              backgroundColor: 'secondary.dark',
            },
            maxWidth: '300px',
            minWidth: 0,
          }}
        >
          {isLoading ? (
            'Moving...'
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
              <span style={{ flexShrink: 0 }}>Move</span>
            </Box>
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
